import { supabase } from '@/integrations/supabase/client';
import { CaseStudyItem, CaseStudySection, CaseStudyCreationMethod } from '@/types/case-study';

export interface CreateCaseStudyData {
  title: string;
  description?: string;
  cover_image?: string;
  status?: 'draft' | 'published';
  is_featured?: boolean;
  slug?: string;
  seo_title?: string;
  seo_description?: string;
  og_image?: string;
  order_index?: number;
  sections?: CaseStudySection[];
  creation_method?: CaseStudyCreationMethod;
}

export interface UpdateCaseStudyData extends Partial<CreateCaseStudyData> {
  id: string;
}

/**
 * Simplified CaseStudyService - matches portfolio architecture
 * Uses JSONB for sections/images instead of separate tables
 * ~90% less code than the old 3-table approach!
 */
export class CaseStudyService {
  
  // ==================== CASE STUDY CRUD ====================
  
  /**
   * Create a new case study
   */
  static async createCaseStudy(data: CreateCaseStudyData, profileId: string): Promise<CaseStudyItem> {
    const { sections = [], slug: _, creation_method, ...caseStudyData } = data; // Exclude slug & handle creation method separately
    const resolvedCreationMethod: CaseStudyCreationMethod = creation_method ?? 'manual';

    // Validate title
    if (!data.title || data.title.trim() === '') {
      throw new Error('Case study title is required and cannot be empty');
    }

    // Generate unique slug - use provided slug or title, ensure uniqueness
    const uniqueSlug = await this.generateSlug(
      data.slug || data.title, // Use provided slug or generate from title
      profileId
    );

    const { data: caseStudy, error } = await supabase
      .from('case_studies')
      .insert({
        ...caseStudyData, // Does not include slug anymore
        profile_id: profileId,
        slug: uniqueSlug, // Use the guaranteed unique slug
        sections: sections, // JSONB column
        creation_method: resolvedCreationMethod,
      })
      .select()
      .single();

    if (error) {
      console.error('[CaseStudyService] Error creating case study:', error);
      throw error;
    }
    return CaseStudyService.mapToCaseStudyItem(caseStudy);
  }

  /**
   * Get a single case study by ID
   */
  static async getCaseStudy(id: string): Promise<CaseStudyItem> {
    const { data: caseStudy, error } = await supabase
      .from('case_studies')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;

    if (!caseStudy) {
      throw new Error('Case study not found');
    }

    return CaseStudyService.mapToCaseStudyItem(caseStudy);
  }

  /**
   * Get a single case study by slug
   */
  static async getCaseStudyBySlug(slug: string): Promise<CaseStudyItem> {
    const { data: caseStudy, error } = await supabase
      .from('case_studies')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published') // Only get published case studies
      .maybeSingle();

    if (error) throw error;

    if (!caseStudy) {
      throw new Error('Case study not found');
    }

    return CaseStudyService.mapToCaseStudyItem(caseStudy);
  }

  /**
   * Update a case study (including sections)
   */
  static async updateCaseStudy(data: UpdateCaseStudyData): Promise<CaseStudyItem> {
    const { id, ...updateData } = data;
    
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.error('CaseStudyService.updateCaseStudy - Auth error:', authError);
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Validate title if it's being updated
    if (updateData.title !== undefined && (!updateData.title || updateData.title.trim() === '')) {
      throw new Error('Case study title is required and cannot be empty');
    }
    
    const { data: caseStudy, error } = await supabase
      .from('case_studies')
      .update(updateData)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('CaseStudyService.updateCaseStudy - Database error:', error);
      throw error;
    }

    if (!caseStudy) {
      throw new Error('Case study not found or update failed');
    }
    return CaseStudyService.mapToCaseStudyItem(caseStudy);
  }

  /**
   * Delete a case study
   */
  static async deleteCaseStudy(id: string): Promise<void> {
    const { error } = await supabase
      .from('case_studies')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  /**
   * Duplicate a case study
   * Creates a copy with "(Copy)" suffix, draft status, and unique slug
   */
  static async duplicateCaseStudy(id: string): Promise<CaseStudyItem> {
    // Get the original case study
    const originalCaseStudy = await this.getCaseStudy(id);

    // Get the profile_id from the original case study
    const { data: originalData, error: fetchError } = await supabase
      .from('case_studies')
      .select('profile_id')
      .eq('id', id)
      .single();

    if (fetchError || !originalData) {
      throw new Error('Failed to fetch original case study profile');
    }

    const profileId = originalData.profile_id;

    // Generate a unique title by checking existing titles
    let baseTitle = originalCaseStudy.title;
    // Remove existing "(Copy)" or "(Copy N)" suffix if present
    baseTitle = baseTitle.replace(/\s*\(Copy(?:\s+\d+)?\)\s*$/, '');

    // Get all existing draft case studies for this user
    const { data: existingCaseStudies } = await supabase
      .from('case_studies')
      .select('title')
      .eq('profile_id', profileId)
      .eq('status', 'draft');

    // Find a unique title
    let copyNumber = 1;
    let newTitle = `${baseTitle} (Copy)`;

    while (existingCaseStudies?.some(cs => cs.title === newTitle)) {
      copyNumber++;
      newTitle = `${baseTitle} (Copy ${copyNumber})`;
    }

    // Create the duplicate with modified data
    const duplicateData: CreateCaseStudyData = {
      title: newTitle,
      description: originalCaseStudy.description,
      cover_image: originalCaseStudy.cover_image,
      status: 'draft', // Always create as draft
      is_featured: false, // Never featured
      sections: originalCaseStudy.sections,
      seo_title: originalCaseStudy.seo_title,
      seo_description: originalCaseStudy.seo_description,
      og_image: originalCaseStudy.og_image,
      creation_method: 'manual',
    };

    // Create the new case study (generateSlug will handle uniqueness)
    return this.createCaseStudy(duplicateData, profileId);
  }

  /**
   * List case studies with optional filters
   */
  static async listCaseStudies(profileId?: string, status?: 'draft' | 'published'): Promise<CaseStudyItem[]> {
    let query = supabase
      .from('case_studies')
      .select('*')
      .order('order_index', { ascending: true });

    if (profileId) {
      query = query.eq('profile_id', profileId);
    }

    if (status) {
      query = query.eq('status', status);
    }

    const { data: caseStudies, error } = await query;

    if (error) throw error;
    return (caseStudies || []).map(CaseStudyService.mapToCaseStudyItem);
  }

  // ==================== SECTION HELPERS ====================
  
  /**
   * Update sections within a case study
   * This replaces the entire sections array
   */
  static async updateSections(caseStudyId: string, sections: CaseStudySection[]): Promise<CaseStudyItem> {
    return this.updateCaseStudy({
      id: caseStudyId,
      sections: sections,
    });
  }

  /**
   * Add a section to a case study
   */
  static async addSection(caseStudyId: string, section: CaseStudySection): Promise<CaseStudyItem> {
    // Get current case study
    const caseStudy = await this.getCaseStudy(caseStudyId);
    
    // Add new section
    const updatedSections = [...caseStudy.sections, section];
    
    // Update case study
    return this.updateCaseStudy({
      id: caseStudyId,
      sections: updatedSections,
    });
  }

  /**
   * Update a specific section
   */
  static async updateSection(caseStudyId: string, sectionId: string, updates: Partial<CaseStudySection>): Promise<CaseStudyItem> {
    // Get current case study
    const caseStudy = await this.getCaseStudy(caseStudyId);
    
    // Update the specific section
    const updatedSections = caseStudy.sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    );
    
    // Update case study
    return this.updateCaseStudy({
      id: caseStudyId,
      sections: updatedSections,
    });
  }

  /**
   * Delete a section from a case study
   */
  static async deleteSection(caseStudyId: string, sectionId: string): Promise<CaseStudyItem> {
    // Get current case study
    const caseStudy = await this.getCaseStudy(caseStudyId);
    
    // Filter out the section
    const updatedSections = caseStudy.sections.filter(section => section.id !== sectionId);
    
    // Update case study
    return this.updateCaseStudy({
      id: caseStudyId,
      sections: updatedSections,
    });
  }

  /**
   * Reorder sections
   */
  static async reorderSections(caseStudyId: string, sections: CaseStudySection[]): Promise<CaseStudyItem> {
    return this.updateCaseStudy({
      id: caseStudyId,
      sections: sections,
    });
  }

  // ==================== UTILITIES ====================

  /**
   * Generate a unique slug for a case study
   * Handles both new titles and existing slugs that might already have numeric suffixes
   */
  static async generateSlug(titleOrSlug: string, profileId: string): Promise<string> {
    // Extract base slug - remove existing numeric suffix if present
    const baseSlug = titleOrSlug
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .replace(/-\d+$/, ''); // Remove trailing -1, -2, etc.

    let slug = baseSlug;
    let counter = 1;

    while (true) {
      const { data: existing, error } = await supabase
        .from('case_studies')
        .select('id')
        .eq('slug', slug)
        .eq('profile_id', profileId);

      // If no rows found or error, slug is available
      if (!existing || existing.length === 0) break;

      slug = `${baseSlug}-${counter}`;
      counter++;
    }

    return slug;
  }

  /**
   * Map database row to CaseStudyItem type
   */
  private static mapToCaseStudyItem(data: Record<string, any>): CaseStudyItem {
    return {
      id: data.id,
      title: data.title,
      description: data.description || '',
      cover_image: data.cover_image || '',
      sections: Array.isArray(data.sections) ? data.sections : [],
      created_at: data.created_at,
      updated_at: data.updated_at,
      is_featured: data.is_featured || false,
      status: data.status || 'draft',
      order: data.order_index || 0,
      slug: data.slug,
      seo_title: data.seo_title,
      seo_description: data.seo_description,
      og_image: data.og_image,
      creation_method: data.creation_method || 'manual',
    };
  }
}
